import { db, auth } from '../config/firebase';
import { DEFAULT_BUENAVISTA_ORG, DEFAULT_SYSTEM_COUNSELORS } from '../constants/peopleCareConstants';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    Timestamp,
    serverTimestamp,
    writeBatch
} from 'firebase/firestore';

const COLLECTIONS = {
    CLIENTS: 'clients',
    WORKS: 'works',
    MEETINGS: 'meetings',
    ASSESSMENTS: 'assessments',
    CREATIVE_WORKS: 'creativeWorks',
    AT_LAB: 'atLab',
    COUNSELORS: 'counselors',
    PUBLIC_INSIGHTS: 'public_insights',
    GROUP_REPORTS: 'group_reports',
    ORGANIZATIONS: 'organizations'
};

export const FirebaseService = {
    // --- GENERIC HELPERS ---

    /**
     * Convert Firestore Timestamp to ISO string in object
     */
    normalizeData: (doc) => {
        const data = doc.data();
        const id = doc.id;

        // Helper to convert timestamps recursively
        const convertTimestamps = (obj) => {
            if (obj === null || typeof obj !== 'object') return obj;

            for (const key in obj) {
                if (obj[key] instanceof Timestamp) {
                    obj[key] = obj[key].toDate().toISOString();
                } else if (typeof obj[key] === 'object') {
                    convertTimestamps(obj[key]);
                }
            }
            return obj;
        };

        return { id, ...convertTimestamps(data) };
    },

    // --- COUNSELORS ---

    getAllCounselors: async () => {
        try {
            const q = query(collection(db, COLLECTIONS.COUNSELORS));
            const querySnapshot = await getDocs(q);
            const rawDocs = querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));
            
            // Auto-seed missing system counselors (e.g. Marzia M., Martina Trebino)
            const existingEmails = new Set(rawDocs.map(d => d.email?.toLowerCase().trim()).filter(Boolean));
            const missingSeed = (DEFAULT_SYSTEM_COUNSELORS || []).filter(sc => !existingEmails.has(sc.email.toLowerCase().trim()));

            if (missingSeed.length > 0) {
                for (const seed of missingSeed) {
                    try {
                        await FirebaseService.createCounselor(
                            seed.id,
                            seed.email,
                            seed.name,
                            seed.role,
                            {
                                canOrganizational: seed.canOrganizational ?? true,
                                specializations: seed.specializations || ['individuale', 'organizzativo'],
                                initialPassword: seed.initialPassword || 'Awareness2026!'
                            }
                        );
                        rawDocs.push({
                            id: seed.id,
                            email: seed.email,
                            name: seed.name,
                            role: seed.role,
                            canOrganizational: seed.canOrganizational ?? true,
                            specializations: seed.specializations || ['individuale', 'organizzativo'],
                            initialPassword: seed.initialPassword || 'Awareness2026!',
                            isActive: true
                        });
                    } catch (seedErr) {
                        console.warn("Counselor auto-seed skipped/failed:", seedErr);
                        rawDocs.push({
                            id: seed.id,
                            email: seed.email,
                            name: seed.name,
                            role: seed.role,
                            canOrganizational: seed.canOrganizational ?? true,
                            specializations: seed.specializations || ['individuale', 'organizzativo'],
                            initialPassword: seed.initialPassword || 'Awareness2026!',
                            isActive: true
                        });
                    }
                }
            }

            // Deduplicate and merge by normalized email
            const counselorMap = new Map();

            for (const doc of rawDocs) {
                if (!doc) continue;
                const emailKey = (doc.email || '').toLowerCase().trim();
                const key = emailKey || doc.id;
                if (!key) continue;

                // Ensure correct display names for system counselors
                if (emailKey === 'martina@gmail.com') {
                    if (!doc.name || doc.name === 'Counselor' || doc.name.toLowerCase().includes('trebino') || doc.name.toLowerCase().includes('tavarone')) {
                        doc.name = 'Martina Vaccarone';
                    }
                } else if (emailKey === 'trebino.martina@gmail.com') {
                    if (!doc.name || doc.name === 'Counselor') {
                        doc.name = 'Martina Trebino';
                    }
                } else if (emailKey === 'patrizia.marchi.1969@gmail.com') {
                    if (!doc.name || doc.name === 'Counselor') {
                        doc.name = 'Patrizia Marchi';
                    }
                }

                // Google OAuth accounts do not use application passwords
                if (emailKey.endsWith('@gmail.com') || emailKey === 'patrizia.marchi.1969@gmail.com') {
                    doc.authMethod = 'google';
                    if (doc.initialPassword) {
                        delete doc.initialPassword;
                    }
                }

                if (!counselorMap.has(key)) {
                    counselorMap.set(key, { ...doc, aliasIds: [doc.id] });
                } else {
                    const existing = counselorMap.get(key);
                    // Determine which is preferred (Firebase Auth UID vs seed id)
                    const isDocAuthUid = doc.id && !doc.id.includes('_counselor') && !doc.id.includes('_admin');
                    const isExistingAuthUid = existing.id && !existing.id.includes('_counselor') && !existing.id.includes('_admin');
                    const preferred = isDocAuthUid ? doc : existing;
                    const secondary = isDocAuthUid ? existing : doc;

                    if (isDocAuthUid && !isExistingAuthUid && secondary?.id && (secondary.id.includes('_counselor') || secondary.id.includes('_admin'))) {
                        // Clean up legacy seed doc from Firestore in background
                        deleteDoc(doc(db, COLLECTIONS.COUNSELORS, secondary.id)).catch(() => {});
                    }

                    const mergedAliases = Array.from(new Set([
                        ...(existing.aliasIds || [existing.id]),
                        doc.id,
                        ...(doc.aliasIds || [])
                    ]));

                    counselorMap.set(key, {
                        ...secondary,
                        ...preferred,
                        name: preferred.name || secondary.name,
                        role: preferred.role || secondary.role,
                        canOrganizational: preferred.canOrganizational ?? secondary.canOrganizational ?? true,
                        specializations: preferred.specializations || secondary.specializations,
                        aliasIds: mergedAliases
                    });
                }
            }

            const uniqueCounselors = Array.from(counselorMap.values()).filter(c => c.isActive !== false);
            uniqueCounselors.sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
            return uniqueCounselors;
        } catch (error) {
            console.error("Error getting all counselors:", error);
            // Fallback to default system counselors if offline / permission blocked
            const fallbackMap = new Map();
            (DEFAULT_SYSTEM_COUNSELORS || []).forEach(sc => {
                const k = sc.email.toLowerCase().trim();
                if (!fallbackMap.has(k)) fallbackMap.set(k, { ...sc, aliasIds: [sc.id] });
            });
            return Array.from(fallbackMap.values());
        }
    },

    getAllCounselorsWithStats: async () => {
        try {
            const counselors = await FirebaseService.getAllCounselors();
            let clientCounts = {};

            try {
                const clientsSnap = await getDocs(query(collection(db, COLLECTIONS.CLIENTS)));
                clientsSnap.forEach(docSnap => {
                    const data = docSnap.data();
                    const cid = data.counselorId || 'unassigned';
                    clientCounts[cid] = (clientCounts[cid] || 0) + 1;
                    if (data.counselorEmail) {
                        const em = data.counselorEmail.toLowerCase().trim();
                        clientCounts[em] = (clientCounts[em] || 0) + 1;
                    }
                });
            } catch (permErr) {
                // If segregation prevents querying all clients, proceed with counselor data
                console.info("Client count skipped due to privacy segregation:", permErr.message);
            }

            return counselors.map(c => {
                const emailKey = c.email?.toLowerCase().trim();
                let total = clientCounts[c.id] || (emailKey ? clientCounts[emailKey] : 0) || c.clientCount || 0;
                if (c.aliasIds && Array.isArray(c.aliasIds)) {
                    c.aliasIds.forEach(aliasId => {
                        if (aliasId !== c.id && clientCounts[aliasId]) {
                            total = Math.max(total, clientCounts[aliasId]);
                        }
                    });
                }
                return {
                    ...c,
                    clientCount: total
                };
            });
        } catch (error) {
            console.error("Error getting counselors with stats:", error);
            throw error;
        }
    },

    getCounselorByUid: async (uid) => {
        try {
            if (!uid) return null;
            const docRef = doc(db, COLLECTIONS.COUNSELORS, uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return FirebaseService.normalizeData(docSnap);
            }
            // Check default system counselors
            const defaultMatch = (DEFAULT_SYSTEM_COUNSELORS || []).find(c => c.id === uid);
            return defaultMatch || null;
        } catch (error) {
            console.error("Error getting counselor by uid:", error);
            const defaultMatch = (DEFAULT_SYSTEM_COUNSELORS || []).find(c => c.id === uid);
            return defaultMatch || null;
        }
    },

    getCounselorByEmail: async (email) => {
        try {
            if (!email) return null;
            const cleanEmail = email.toLowerCase().trim();
            const q = query(
                collection(db, COLLECTIONS.COUNSELORS),
                where("email", "==", cleanEmail)
            );
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                return FirebaseService.normalizeData(querySnapshot.docs[0]);
            }
            // Check default system counselors
            const defaultMatch = (DEFAULT_SYSTEM_COUNSELORS || []).find(c => c.email.toLowerCase() === cleanEmail);
            return defaultMatch || null;
        } catch (error) {
            console.error("Error getting counselor by email:", error);
            const defaultMatch = (DEFAULT_SYSTEM_COUNSELORS || []).find(c => c.email.toLowerCase() === email?.toLowerCase()?.trim());
            return defaultMatch || null;
        }
    },

    createCounselor: async (uid, email, name, role = 'counselor', extraData = {}) => {
        try {
            const docRef = doc(db, COLLECTIONS.COUNSELORS, uid);
            const canOrg = extraData.canOrganizational !== undefined ? Boolean(extraData.canOrganizational) : true;
            const newCounselor = {
                id: uid,
                email: email.toLowerCase(),
                name: name || email.split('@')[0],
                role: role, // 'counselor' | 'admin'
                canOrganizational: canOrg,
                specializations: extraData.specializations || (canOrg ? ['individuale', 'organizzativo'] : ['individuale']),
                isActive: true,
                ...extraData,
                createdAt: Timestamp.now()
            };
            await setDoc(docRef, newCounselor, { merge: true });
            return { ...newCounselor, createdAt: newCounselor.createdAt.toDate().toISOString() };
        } catch (error) {
            console.error("Error creating counselor:", error);
            throw error;
        }
    },

    updateCounselor: async (uid, updateData) => {
        try {
            const counselorRef = doc(db, COLLECTIONS.COUNSELORS, uid);

            if (updateData.email) {
                updateData.email = updateData.email.toLowerCase();
                const batch = writeBatch(db);
                batch.update(counselorRef, updateData);

                const clientsQuery = query(
                    collection(db, COLLECTIONS.CLIENTS),
                    where("counselorId", "==", uid)
                );
                const clientsSnap = await getDocs(clientsQuery);

                clientsSnap.forEach(clientDoc => {
                    batch.update(clientDoc.ref, { counselorEmail: updateData.email });
                });

                await batch.commit();
            } else {
                await updateDoc(counselorRef, updateData);
            }

            return { id: uid, ...updateData };
        } catch (error) {
            console.error("Error updating counselor:", error);
            throw error;
        }
    },

    deleteCounselor: async (uid) => {
        try {
            await deleteDoc(doc(db, COLLECTIONS.COUNSELORS, uid));
            return true;
        } catch (error) {
            console.error("Error deleting counselor:", error);
            throw error;
        }
    },

    reassignClientCounselor: async (clientId, newCounselorId, newCounselorEmail = '', newCounselorName = '') => {
        try {
            if (!clientId || !newCounselorId) {
                throw new Error("ID Cliente e ID Counselor di destinazione sono obbligatori.");
            }
            const clientRef = doc(db, COLLECTIONS.CLIENTS, clientId);
            
            let targetEmail = newCounselorEmail ? newCounselorEmail.toLowerCase().trim() : '';
            let targetName = newCounselorName ? newCounselorName.trim() : '';
            let targetUid = newCounselorId;

            // Fetch counselor document to get complete details
            const cDoc = await FirebaseService.getCounselorByUid(newCounselorId) || (targetEmail ? await FirebaseService.getCounselorByEmail(targetEmail) : null);
            if (cDoc) {
                if (!targetEmail && cDoc.email) targetEmail = cDoc.email.toLowerCase().trim();
                if (!targetName && cDoc.name) targetName = cDoc.name.trim();
                if (cDoc.id) targetUid = cDoc.id;
            }

            const updatePayload = {
                counselorId: targetUid,
                updatedAt: Timestamp.now()
            };
            if (targetEmail) {
                updatePayload.counselorEmail = targetEmail;
            }
            if (targetName) {
                updatePayload.counselorName = targetName;
            }

            await updateDoc(clientRef, updatePayload);
            return true;
        } catch (error) {
            console.error("Error reassigning client:", error);
            throw error;
        }
    },

    // --- CLIENTS ---

    /**
     * Query filtrata per counselorId o per Admin.
     * Permette anche ad un Admin di filtrare per uno specifico counselorId.
     */
    getAllClients: async (filterCounselorId = null) => {
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("User not authenticated");

            const currentEmail = currentUser.email?.toLowerCase().trim() || '';
            const ADMIN_EMAILS = ['nuvola@awarenesstool.it', 'marilisa.delve@gmail.com'];
            const BUENAVISTA_ADMIN_EMAILS = ['patrizia.marforio@gmail.com', 'marzia.macca@gmail.com', 'trebino.martina@gmail.com', 'patrizia.marchi.1969@gmail.com'];
            let isAdmin = ADMIN_EMAILS.includes(currentEmail);
            let isOrgAdmin = BUENAVISTA_ADMIN_EMAILS.includes(currentEmail);
            let allowedOrgs = isOrgAdmin ? ['buenavista'] : [];

            const cDoc = await FirebaseService.getCounselorByUid(currentUser.uid) || await FirebaseService.getCounselorByEmail(currentEmail);
            if (cDoc?.role === 'admin' || cDoc?.role === 'superadmin' || cDoc?.isAdmin === true) {
                isAdmin = true;
            } else if (cDoc?.role === 'org_admin') {
                isOrgAdmin = true;
                allowedOrgs = cDoc.allowedOrganizations || (cDoc.organizationId ? [cDoc.organizationId] : ['buenavista']);
            }

            let docs = [];

            if (isAdmin || isOrgAdmin) {
                const q = query(collection(db, COLLECTIONS.CLIENTS));
                const querySnapshot = await getDocs(q);
                docs = querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));

                // Scoping per Organization Admin (Patrizia Marforio, Marzia M., Martina Trebino per BuenaVista)
                // "Mi raccomando, solo di loro. tutto il resto di awarenesstool rimane segregato come sempre."
                if (isOrgAdmin && !isAdmin) {
                    docs = docs.filter(c => {
                        const clientOrg = (c.organization || '').toLowerCase().trim();
                        const clientOrgName = (c.organizationName || '').toLowerCase().trim();
                        const settingType = (c.settingType || '').toLowerCase().trim();
                        const isBuenaVista = clientOrg === 'buenavista' || 
                                             clientOrgName.includes('buenavista') || 
                                             allowedOrgs.some(org => clientOrg === org.toLowerCase() || clientOrgName.includes(org.toLowerCase()));
                        
                        // Possono anche vedere i propri clienti individuali assegnati a loro
                        const isOwnClient = c.counselorId === currentUser.uid ||
                                            (currentEmail && (c.counselorEmail?.toLowerCase().trim() === currentEmail || c.counselorId?.toLowerCase().trim() === currentEmail));

                        return isBuenaVista || isOwnClient;
                    });
                }

                if (filterCounselorId && filterCounselorId !== 'all') {
                    // Match by counselorId, counselorEmail, or alternate alias IDs
                    const allCounselors = await FirebaseService.getAllCounselors();
                    const targetC = allCounselors.find(c => c.id === filterCounselorId || c.email?.toLowerCase() === filterCounselorId.toLowerCase());
                    const targetEmail = targetC?.email?.toLowerCase() || '';
                    const matchIds = new Set([filterCounselorId, targetC?.id, ...(targetC?.aliasIds || [])].filter(Boolean));

                    docs = docs.filter(c => 
                        matchIds.has(c.counselorId) ||
                        (targetEmail && (c.counselorEmail?.toLowerCase() === targetEmail || c.counselorId?.toLowerCase() === targetEmail))
                    );
                }
            } else {
                // Counselor standard: vede SOLO i propri clienti assegnati per UID o Email (segregazione totale)
                const qUid = query(
                    collection(db, COLLECTIONS.CLIENTS),
                    where("counselorId", "==", currentUser.uid)
                );
                const querySnapshot = await getDocs(qUid);
                docs = querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));
                const seenIds = new Set(docs.map(d => d.id));

                if (currentEmail) {
                    try {
                        const qEmail = query(
                            collection(db, COLLECTIONS.CLIENTS),
                            where("counselorEmail", "==", currentEmail)
                        );
                        const emailSnap = await getDocs(qEmail);
                        emailSnap.forEach(docSnap => {
                            if (!seenIds.has(docSnap.id)) {
                                seenIds.add(docSnap.id);
                                docs.push(FirebaseService.normalizeData(docSnap));
                            }
                        });
                    } catch (e) {
                        console.info("Client query by email skipped:", e.message);
                    }
                }
            }

            // DEDUPLICAZIONE E MERGE DEI CLIENTI
            // Previene la presenza di clienti duplicati nella console admin e nella lista counselor
            // Unisce i record SOLO se rappresentano la stessa identica persona (stessa email E stesso nome / senza nome).
            // NON unisce MAI persone con nomi diversi che condividono per errore un'email (es. email del counselor usata come placeholder o per clienti manuali).
            const clientMap = new Map();
            for (const doc of docs) {
                if (!doc || doc.isArchived === true) continue;
                const emailKey = (doc.email || '').toLowerCase().trim();
                const normName = (doc.name || '').toLowerCase().replace(/\s+/g, ' ').trim();

                let matchedKey = null;
                if (emailKey) {
                    for (const [existingKey, existingDoc] of clientMap.entries()) {
                        const existingEmail = (existingDoc.email || '').toLowerCase().trim();
                        if (existingEmail === emailKey) {
                            const existingNormName = (existingDoc.name || '').toLowerCase().replace(/\s+/g, ' ').trim();
                            // Stesso nome (o uno dei due non ha nome specificato) -> considerati la stessa persona
                            if (!normName || !existingNormName || normName === existingNormName) {
                                matchedKey = existingKey;
                                break;
                            }
                        }
                    }
                }

                if (!matchedKey) {
                    clientMap.set(doc.id, { ...doc, aliasIds: [doc.id] });
                } else {
                    const existing = clientMap.get(matchedKey);
                    // Determina quale documento privilegiare (Auth UID vs ID generato da counselor)
                    const isDocUid = doc.id && doc.id.length > 20 && !doc.id.startsWith('client_');
                    const isExistingUid = existing.id && existing.id.length > 20 && !existing.id.startsWith('client_');
                    const preferred = isDocUid && !isExistingUid ? doc : existing;
                    const secondary = preferred === doc ? existing : doc;

                    const mergedAliases = Array.from(new Set([
                        ...(existing.aliasIds || [existing.id]),
                        doc.id,
                        ...(doc.aliasIds || [])
                    ]));

                    clientMap.set(matchedKey, {
                        ...secondary,
                        ...preferred,
                        settingType: preferred.settingType || secondary.settingType,
                        organization: preferred.organization || secondary.organization,
                        organizationName: preferred.organizationName || secondary.organizationName,
                        counselorId: preferred.counselorId || secondary.counselorId,
                        counselorEmail: preferred.counselorEmail || secondary.counselorEmail,
                        counselorName: preferred.counselorName || secondary.counselorName,
                        mode: preferred.mode || secondary.mode,
                        aliasIds: mergedAliases
                    });
                }
            }

            return Array.from(clientMap.values());
        } catch (error) {
            console.error("Error getting clients:", error);
            throw error;
        }
    },

    getClientById: async (clientId) => {
        try {
            const docRef = doc(db, COLLECTIONS.CLIENTS, clientId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return FirebaseService.normalizeData(docSnap);
            }
            return null;
        } catch (error) {
            console.error("Error getting client by id:", error);
            throw error;
        }
    },

    /**
     * Lookup diretto per UID Firebase Auth — O(1), nessuna query.
     * Questo è il metodo primario per trovare un cliente autenticato.
     * L'UID corrisponde esattamente all'ID del documento Firestore.
     */
    getClientByUid: async (uid) => {
        try {
            const docRef = doc(db, COLLECTIONS.CLIENTS, uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return FirebaseService.normalizeData(docSnap);
            }
            return null;
        } catch (error) {
            console.error("Error getting client by uid:", error);
            throw error;
        }
    },

    /**
     * @deprecated Usa getClientByUid() — questo metodo è mantenuto solo per
     * compatibilità temporanea con vecchi documenti non ancora migrati.
     * Rimuovere dopo la migrazione completa.
     */
    getClientByEmail: async (email) => {
        console.warn('[DEPRECATED] getClientByEmail: usa getClientByUid(uid) dopo la migrazione.');
        try {
            // Fallback query per documenti legacy con ID casuale o email-based
            try {
                const q = query(
                    collection(db, COLLECTIONS.CLIENTS),
                    where("email", "==", email.toLowerCase())
                );
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    return FirebaseService.normalizeData(querySnapshot.docs[0]);
                }
            } catch (_) {
                // Query bloccata da Firestore rules (ruolo client) — atteso, non è un errore
            }
            return null;
        } catch (error) {
            console.error("Error getting client by email:", error);
            throw error;
        }
    },

    /**
     * Crea un documento cliente usando l'UID Firebase Auth come Document ID.
     * Garantisce un mapping 1:1 tra Firebase Auth e Firestore: il documento
     * è sempre trovabile con getDoc(uid) senza richiedere query.
     *
     * @param {Object} clientData - Dati del cliente. DEVE contenere:
     *   - firebaseUid {string} — UID Firebase Auth del cliente (usato come Document ID)
     *   - email {string}
     *   - name {string}
     */
    createClient: async (clientData) => {
        try {
            const cleanEmail = (clientData.email || '').toLowerCase().trim();
            const clientNameNorm = (clientData.name || '').toLowerCase().replace(/\s+/g, ' ').trim();
            if (cleanEmail) {
                const existingByEmail = await FirebaseService.getClientByEmail(cleanEmail);
                const existingNameNorm = (existingByEmail?.name || '').toLowerCase().replace(/\s+/g, ' ').trim();
                // Aggiorna solo se è lo stesso identico cliente (stesso nome o nome non specificato)
                if (existingByEmail && (!existingNameNorm || !clientNameNorm || existingNameNorm === clientNameNorm)) {
                    console.info("Client with this email and name already exists, updating to avoid duplication:", cleanEmail);
                    const updatePayload = { ...clientData };
                    delete updatePayload.firebaseUid;
                    await updateDoc(doc(db, COLLECTIONS.CLIENTS, existingByEmail.id), {
                        ...updatePayload,
                        updatedAt: Timestamp.now()
                    });
                    return { ...existingByEmail, ...updatePayload };
                }
            }

            const uid = clientData.firebaseUid || doc(collection(db, COLLECTIONS.CLIENTS)).id;
            const docRef = doc(db, COLLECTIONS.CLIENTS, uid);

            // Se esiste già, restituisce senza sovrascrivere
            try {
                const existing = await getDoc(docRef);
                if (existing.exists()) {
                    return FirebaseService.normalizeData(existing);
                }
            } catch (readError) {
                // Se non abbiamo i permessi di lettura (es. counselor non admin per documento inesistente), 
                // procediamo comunque con il salvataggio. Le regole di Firestore bloccheranno il setDoc
                // se il documento esiste già e appartiene a qualcun altro.
                if (readError.code !== 'permission-denied') {
                    throw readError;
                }
            }

            // Recupera UID e email del counselor autenticato
            const finalData = { ...clientData };
            delete finalData.firebaseUid; // non serve nel documento, è già l'ID

            if (auth.currentUser) {
                if (!finalData.counselorId) {
                    finalData.counselorId = auth.currentUser.uid;
                }

                const counselorRef = doc(db, COLLECTIONS.COUNSELORS, finalData.counselorId);
                const counselorSnap = await getDoc(counselorRef);
                finalData.counselorEmail = counselorSnap.exists()
                    ? counselorSnap.data().email
                    : (finalData.counselorId === auth.currentUser.uid ? auth.currentUser.email.toLowerCase() : '');
            }

            const newClient = {
                ...finalData,
                id: uid,
                email: clientData.email.toLowerCase(),
                createdAt: Timestamp.now()
            };

            await setDoc(docRef, newClient);
            return FirebaseService.normalizeData({ id: uid, data: () => newClient });
        } catch (error) {
            console.error("Error creating client:", error);
            throw error;
        }
    },

    updateClient: async (clientId, updateData) => {
        try {
            const clientRef = doc(db, COLLECTIONS.CLIENTS, clientId);

            try {
                // Tentativo A: Aggiornamento standard (99% dei casi)
                await updateDoc(clientRef, updateData);
                return { id: clientId, ...updateData };
            } catch (innerError) {
                // Se Firestore lancia l'errore che "non c'è un documento", significa che
                // è stato piallato o dalla console o il database locale è in desync.
                if (innerError.message && innerError.message.includes('No document to update')) {
                    // ⚠️ RESURRECTION MODE FORCE: Ricreiamo il documento istantaneamente
                    const finalUpdate = { ...updateData };
                    if (auth.currentUser) {
                        finalUpdate.counselorId = auth.currentUser.uid;
                        finalUpdate.counselorEmail = auth.currentUser.email.toLowerCase();
                    }
                    if (!finalUpdate.createdAt) {
                        finalUpdate.createdAt = Timestamp.now();
                    }
                    await setDoc(clientRef, finalUpdate, { merge: true });
                    return { id: clientId, ...finalUpdate };
                }
                throw innerError;
            }
        } catch (error) {
            console.error("Error updating client:", error);
            throw error;
        }
    },

    deleteClient: async (clientId) => {
        try {
            await deleteDoc(doc(db, COLLECTIONS.CLIENTS, clientId));
            // Note: Cloud functions would be better for cascade deletion
            return true;
        } catch (error) {
            console.error("Error deleting client:", error);
            throw error;
        }
    },

    // --- SUPERADMIN ACCESS AUTHORIZATION (DEONTOLOGICAL PRIVACY) ---

    requestSuperAdminAccess: async (clientId, requesterInfo = {}) => {
        try {
            const client = await FirebaseService.getClientById(clientId);
            const history = client?.superAdminAccessLog || [];
            const logItem = {
                id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                action: 'requested',
                timestamp: new Date().toISOString(),
                userEmail: requesterInfo.requestedBy || auth.currentUser?.email || 'SuperAdmin',
                userName: requesterInfo.requestedByName || auth.currentUser?.displayName || requesterInfo.requestedBy || 'SuperAdmin',
                notes: requesterInfo.notes || ''
            };

            const payload = {
                superAdminAccessStatus: 'pending',
                superAdminAccessRequestedAt: logItem.timestamp,
                superAdminAccessRequestedBy: logItem.userEmail,
                superAdminAccessRequestedByName: logItem.userName,
                superAdminAccessNotes: logItem.notes,
                superAdminAccessLog: [logItem, ...history]
            };
            await FirebaseService.updateClient(clientId, payload);
            return payload;
        } catch (error) {
            console.error("Error requesting superadmin access:", error);
            throw error;
        }
    },

    approveSuperAdminAccess: async (clientId, approverInfo = {}) => {
        try {
            const client = await FirebaseService.getClientById(clientId);
            const history = client?.superAdminAccessLog || [];
            const logItem = {
                id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                action: 'approved',
                timestamp: new Date().toISOString(),
                userEmail: approverInfo.approvedBy || auth.currentUser?.email || 'Counselor',
                userName: approverInfo.approvedByName || approverInfo.name || auth.currentUser?.displayName || 'Counselor',
                notes: approverInfo.notes || ''
            };

            const payload = {
                superAdminAccessStatus: 'approved',
                superAdminAccessApprovedAt: logItem.timestamp,
                superAdminAccessApprovedBy: logItem.userEmail,
                superAdminAccessApprovedByName: logItem.userName,
                superAdminAccessLog: [logItem, ...history]
            };
            await FirebaseService.updateClient(clientId, payload);
            return payload;
        } catch (error) {
            console.error("Error approving superadmin access:", error);
            throw error;
        }
    },

    rejectSuperAdminAccess: async (clientId, rejectorInfo = {}) => {
        try {
            const client = await FirebaseService.getClientById(clientId);
            const history = client?.superAdminAccessLog || [];
            const logItem = {
                id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                action: 'rejected',
                timestamp: new Date().toISOString(),
                userEmail: rejectorInfo.rejectedBy || auth.currentUser?.email || 'Counselor',
                userName: rejectorInfo.rejectedByName || rejectorInfo.name || auth.currentUser?.displayName || 'Counselor',
                notes: rejectorInfo.notes || ''
            };

            const payload = {
                superAdminAccessStatus: 'rejected',
                superAdminAccessRejectedAt: logItem.timestamp,
                superAdminAccessRejectedBy: logItem.userEmail,
                superAdminAccessRejectedByName: logItem.userName,
                superAdminAccessLog: [logItem, ...history]
            };
            await FirebaseService.updateClient(clientId, payload);
            return payload;
        } catch (error) {
            console.error("Error rejecting superadmin access:", error);
            throw error;
        }
    },

    revokeSuperAdminAccess: async (clientId, revokerInfo = {}) => {
        try {
            const client = await FirebaseService.getClientById(clientId);
            const history = client?.superAdminAccessLog || [];
            const logItem = {
                id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                action: 'revoked',
                timestamp: new Date().toISOString(),
                userEmail: revokerInfo.revokedBy || auth.currentUser?.email || 'Counselor',
                userName: revokerInfo.revokedByName || revokerInfo.name || auth.currentUser?.displayName || 'Counselor',
                notes: revokerInfo.notes || ''
            };

            const payload = {
                superAdminAccessStatus: 'none',
                superAdminAccessRevokedAt: logItem.timestamp,
                superAdminAccessRevokedBy: logItem.userEmail,
                superAdminAccessRevokedByName: logItem.userName,
                superAdminAccessLog: [logItem, ...history]
            };
            await FirebaseService.updateClient(clientId, payload);
            return payload;
        } catch (error) {
            console.error("Error revoking superadmin access:", error);
            throw error;
        }
    },

    cancelSuperAdminAccessRequest: async (clientId) => {
        try {
            const client = await FirebaseService.getClientById(clientId);
            const history = client?.superAdminAccessLog || [];
            const logItem = {
                id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                action: 'cancelled',
                timestamp: new Date().toISOString(),
                userEmail: auth.currentUser?.email || 'SuperAdmin',
                userName: auth.currentUser?.displayName || auth.currentUser?.email || 'SuperAdmin',
                notes: 'Richiesta annullata dal SuperAdmin'
            };

            const payload = {
                superAdminAccessStatus: 'none',
                superAdminAccessRequestedAt: null,
                superAdminAccessRequestedBy: null,
                superAdminAccessNotes: '',
                superAdminAccessLog: [logItem, ...history]
            };
            await FirebaseService.updateClient(clientId, payload);
            return payload;
        } catch (error) {
            console.error("Error cancelling superadmin access request:", error);
            throw error;
        }
    },

    getAllSuperAdminAccessLogs: async (clientsList = null) => {
        try {
            const clients = clientsList || await FirebaseService.getAllClients();
            const logs = [];

            (clients || []).forEach(c => {
                // If client has structured log
                if (Array.isArray(c.superAdminAccessLog) && c.superAdminAccessLog.length > 0) {
                    c.superAdminAccessLog.forEach(item => {
                        logs.push({
                            ...item,
                            clientId: c.id,
                            clientName: c.name || 'Cliente senza nome',
                            clientEmail: c.email || '',
                            counselorId: c.counselorId,
                            counselorEmail: c.counselorEmail,
                            counselorName: c.counselorName,
                            currentStatus: c.superAdminAccessStatus || 'none'
                        });
                    });
                } else if (c.superAdminAccessStatus && c.superAdminAccessStatus !== 'none') {
                    // Legacy/Implicit entry if log array wasn't yet created
                    logs.push({
                        id: `legacy_${c.id}`,
                        action: c.superAdminAccessStatus,
                        timestamp: c.superAdminAccessApprovedAt || c.superAdminAccessRequestedAt || c.updatedAt || new Date().toISOString(),
                        userEmail: c.superAdminAccessApprovedBy || c.superAdminAccessRequestedBy || 'SuperAdmin',
                        userName: c.superAdminAccessApprovedByName || c.superAdminAccessRequestedByName || 'SuperAdmin',
                        notes: c.superAdminAccessNotes || '',
                        clientId: c.id,
                        clientName: c.name || 'Cliente senza nome',
                        clientEmail: c.email || '',
                        counselorId: c.counselorId,
                        counselorEmail: c.counselorEmail,
                        counselorName: c.counselorName,
                        currentStatus: c.superAdminAccessStatus
                    });
                }
            });

            return logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        } catch (error) {
            console.error("Error fetching access logs:", error);
            return [];
        }
    },

    /**
     * Changes the Client ID (which is the document ID) in Firestore.
     * This creates a new document, copies data, deletes the old one,
     * and cascades the change to all related collections.
     */
    changeClientId: async (oldId, newId) => {
        try {
            if (oldId === newId) return true;

            // 1. Get old client document
            const oldClientRef = doc(db, COLLECTIONS.CLIENTS, oldId);
            const oldClientSnap = await getDoc(oldClientRef);
            
            if (!oldClientSnap.exists()) {
                throw new Error("Cliente originale non trovato.");
            }
            
            // 2. Write new client document
            const newClientRef = doc(db, COLLECTIONS.CLIENTS, newId);
            const data = oldClientSnap.data();
            data.id = newId; // Update internal field just in case
            await setDoc(newClientRef, data);
            
            // 3. Update related collections
            const collectionsToUpdate = [
                COLLECTIONS.WORKS,
                COLLECTIONS.MEETINGS,
                COLLECTIONS.ASSESSMENTS,
                COLLECTIONS.CREATIVE_WORKS,
                COLLECTIONS.AT_LAB
            ];
            
            for (const colName of collectionsToUpdate) {
                try {
                    const q = query(collection(db, colName), where("clientId", "==", oldId));
                    const snap = await getDocs(q);
                    
                    // We'll update them individually mapping promises
                    const updatePromises = snap.docs.map(itemDoc => {
                         return updateDoc(itemDoc.ref, { clientId: newId });
                    });
                    
                    await Promise.all(updatePromises);
                } catch (colErr) {
                    console.warn(`Could not cascade clientId update for collection ${colName}:`, colErr);
                }
            }
            
            // 4. Delete old client document (se i permessi lo consentono)
            try {
                await deleteDoc(oldClientRef);
            } catch (delErr) {
                console.warn("Could not delete old client doc (may require counselor/admin permissions):", delErr);
            }
            return true;
            
        } catch (error) {
            console.error("Error changing client id:", error);
            throw error;
        }
    },

    // --- WORKS (Diari, Questionari) ---

    getWorksByClient: async (clientId, counselorView = false) => {
        try {
            let q = query(
                collection(db, COLLECTIONS.WORKS),
                where("clientId", "==", clientId)
            );

            const querySnapshot = await getDocs(q);
            let works = querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));

            // Client-side sort (newest first)
            works.sort((a, b) => {
                const tA = a.timestamp || "";
                const tB = b.timestamp || "";
                return tB.localeCompare(tA);
            });

            // Counselor sees ALL works for synthesis (the 'shared' logic is now handled in the UI if needed)
            // Removed: if (counselorView) { works = works.filter(w => w.shared === true); }
            
            return works;
        } catch (error) {
            console.error("Error getting works:", error);
            throw error;
        }
    },

    saveWork: async (workData) => {
        try {
            const docRef = doc(collection(db, COLLECTIONS.WORKS));

            let ts = Timestamp.now();
            if (workData.timestamp) {
                ts = typeof workData.timestamp === 'string' ?
                    Timestamp.fromDate(new Date(workData.timestamp)) :
                    workData.timestamp;
            }

            const newWork = {
                ...workData,
                id: docRef.id,
                timestamp: ts
            };
            await setDoc(docRef, newWork);
            return FirebaseService.normalizeData({ id: docRef.id, data: () => newWork });
        } catch (error) {
            console.error("Error saving work:", error);
            throw error;
        }
    },

    getWorkById: async (workId) => {
        try {
            if (!workId) return null;
            const docRef = doc(db, COLLECTIONS.WORKS, workId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return FirebaseService.normalizeData(docSnap);
            }
            return null;
        } catch (error) {
            console.error("Error getting work by id:", error);
            throw error;
        }
    },

    updateWork: async (workId, updateData) => {
        try {
            const workRef = doc(db, COLLECTIONS.WORKS, workId);
            const data = {
                ...updateData,
                updatedAt: Timestamp.now()
            };
            const cleanData = Object.fromEntries(
                Object.entries(data).filter(([_, v]) => v !== undefined)
            );
            await updateDoc(workRef, cleanData);
            return { id: workId, ...cleanData };
        } catch (error) {
            console.error("Error updating work:", error);
            throw error;
        }
    },

    deleteWork: async (workId) => {
        try {
            await deleteDoc(doc(db, COLLECTIONS.WORKS, workId));
            return true;
        } catch (error) {
            console.error("Error deleting work:", error);
            throw error;
        }
    },

    // --- MEETINGS ---

    getMeetingsByClient: async (clientId, clientEmail = null, clientName = null) => {
        try {
            const meetingsRef = collection(db, COLLECTIONS.MEETINGS);
            const foundMeetings = [];
            const existingIds = new Set();

            const addDocs = (querySnapshot) => {
                if (!querySnapshot || !querySnapshot.docs) return;
                querySnapshot.docs.forEach(docSnap => {
                    if (!existingIds.has(docSnap.id)) {
                        existingIds.add(docSnap.id);
                        foundMeetings.push(FirebaseService.normalizeData(docSnap));
                    }
                });
            };

            // 1. Query by clientId
            if (clientId) {
                try {
                    const snap = await getDocs(query(meetingsRef, where("clientId", "==", clientId)));
                    addDocs(snap);
                } catch (e) {
                    console.info("Meeting query by clientId error:", e.message);
                }
            }

            // 2. Query by clientEmail (exact, lowercase, and as clientId)
            if (clientEmail) {
                const normEmail = clientEmail.toLowerCase().trim();
                const candidates = Array.from(new Set([clientEmail, normEmail].filter(Boolean)));
                for (const em of candidates) {
                    try {
                        const snapEmail = await getDocs(query(meetingsRef, where("clientEmail", "==", em)));
                        addDocs(snapEmail);
                    } catch (e) {}

                    if (em !== clientId) {
                        try {
                            const snapId = await getDocs(query(meetingsRef, where("clientId", "==", em)));
                            addDocs(snapId);
                        } catch (e) {}
                    }
                }
            }

            // 3. Query by clientName
            if (clientName) {
                const trimmedName = clientName.trim();
                const nameCandidates = Array.from(new Set([trimmedName, trimmedName.toLowerCase()].filter(Boolean)));
                for (const nm of nameCandidates) {
                    try {
                        const snapName = await getDocs(query(meetingsRef, where("clientName", "==", nm)));
                        addDocs(snapName);
                    } catch (e) {}

                    if (nm !== clientId) {
                        try {
                            const snapId = await getDocs(query(meetingsRef, where("clientId", "==", nm)));
                            addDocs(snapId);
                        } catch (e) {}
                    }
                }
            }

            // 4. Fallback: if no meetings found by indexed queries, scan all meetings
            if (foundMeetings.length === 0) {
                try {
                    const allMeetings = await FirebaseService.getAllMeetings();
                    const normId = (clientId || '').toLowerCase().trim();
                    const normEmail = (clientEmail || '').toLowerCase().trim();
                    const normName = (clientName || '').toLowerCase().trim();

                    allMeetings.forEach(m => {
                        const mCid = (m.clientId || '').toLowerCase().trim();
                        const mEmail = (m.clientEmail || '').toLowerCase().trim();
                        const mName = (m.clientName || '').toLowerCase().trim();

                        const matches = 
                            (normId && (mCid === normId || mEmail === normId)) ||
                            (normEmail && (mCid === normEmail || mEmail === normEmail)) ||
                            (normName && (mCid === normName || mName === normName || (mName && normName && (mName.includes(normName) || normName.includes(mName)))));

                        if (matches && !existingIds.has(m.id)) {
                            existingIds.add(m.id);
                            foundMeetings.push(m);
                        }
                    });
                } catch (allErr) {
                    console.info("Meeting fallback query all skipped:", allErr.message);
                }
            }

            const parseDateSafe = (dateStr) => {
                if (!dateStr) return 0;

                if (typeof dateStr === 'string' && dateStr.includes('/')) {
                    const parts = dateStr.split('/');
                    if (parts.length === 3) {
                        const day = parseInt(parts[0], 10);
                        const month = parseInt(parts[1], 10) - 1;
                        const year = parseInt(parts[2], 10);

                        if (year > 1000) {
                            return new Date(year, month, day).getTime();
                        } else if (day > 1000) {
                            return new Date(day, month, year).getTime();
                        }
                    }
                }

                const d = new Date(dateStr);
                return isNaN(d.getTime()) ? 0 : d.getTime();
            };

            foundMeetings.sort((a, b) => parseDateSafe(a.date) - parseDateSafe(b.date));

            return foundMeetings;
        } catch (error) {
            console.error("Error getting meetings:", error);
            throw error;
        }
    },

    getAllMeetings: async () => {
        try {
            const querySnapshot = await getDocs(collection(db, COLLECTIONS.MEETINGS));
            return querySnapshot.docs
                .map(doc => FirebaseService.normalizeData(doc))
                .filter(m => !m.deletedAt);
        } catch (error) {
            console.error("Error getting all meetings:", error);
            return [];
        }
    },

    saveMeeting: async (meetingData) => {
        try {
            const docRef = doc(collection(db, COLLECTIONS.MEETINGS));
            const newMeeting = {
                ...meetingData,
                id: docRef.id,
                createdAt: Timestamp.now(),
            };
            await setDoc(docRef, newMeeting);
            return FirebaseService.normalizeData({ id: docRef.id, data: () => newMeeting });
        } catch (error) {
            console.error("Error saving meeting:", error);
            throw error;
        }
    },

    getMeetingById: async (meetingId) => {
        try {
            if (!meetingId) return null;
            const docRef = doc(db, COLLECTIONS.MEETINGS, meetingId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return FirebaseService.normalizeData(docSnap);
            }
            return null;
        } catch (error) {
            console.error("Error getting meeting by id:", error);
            throw error;
        }
    },

    updateMeeting: async (meetingId, updateData) => {
        try {
            const meetingRef = doc(db, COLLECTIONS.MEETINGS, meetingId);
            const data = {
                ...updateData,
                updatedAt: Timestamp.now()
            };
            const cleanData = Object.fromEntries(
                Object.entries(data).filter(([_, v]) => v !== undefined)
            );
            await updateDoc(meetingRef, cleanData);
            return { id: meetingId, ...cleanData };
        } catch (error) {
            console.error("Error updating meeting:", error);
            throw error;
        }
    },

    deleteMeeting: async (meetingId) => {
        try {
            await deleteDoc(doc(db, COLLECTIONS.MEETINGS, meetingId));
            return true;
        } catch (error) {
            console.error("Error deleting meeting:", error);
            throw error;
        }
    },

    // --- ASSESSMENTS (Wheel of Life) ---

    saveAssessment: async (assessmentData) => {
        try {
            const docRef = doc(collection(db, COLLECTIONS.ASSESSMENTS));

            let ts = Timestamp.now();
            if (assessmentData.timestamp) {
                ts = typeof assessmentData.timestamp === 'string' ?
                    Timestamp.fromDate(new Date(assessmentData.timestamp)) :
                    assessmentData.timestamp;
            }

            Object.keys(assessmentData).forEach(key => assessmentData[key] === undefined && delete assessmentData[key]);

            const newAssessment = {
                ...assessmentData,
                id: docRef.id,
                timestamp: ts
            };
            await setDoc(docRef, newAssessment);
            return FirebaseService.normalizeData({ id: docRef.id, data: () => newAssessment });
        } catch (error) {
            console.error("Error saving assessment:", error);
            throw error;
        }
    },

    updateAssessment: async (assessmentId, updateData) => {
        try {
            const assessmentRef = doc(db, COLLECTIONS.ASSESSMENTS, assessmentId);
            const data = {
                ...updateData,
                updatedAt: Timestamp.now()
            };
            await updateDoc(assessmentRef, data);
            return { id: assessmentId, ...data };
        } catch (error) {
            console.error("Error updating assessment:", error);
            throw error;
        }
    },

    deleteAssessment: async (assessmentId) => {
        try {
            await deleteDoc(doc(db, COLLECTIONS.ASSESSMENTS, assessmentId));
            return true;
        } catch (error) {
            console.error("Error deleting assessment:", error);
            throw error;
        }
    },

    getAssessmentsByClient: async (clientId) => {
        try {
            const q = query(
                collection(db, COLLECTIONS.ASSESSMENTS),
                where("clientId", "==", clientId)
            );

            const querySnapshot = await getDocs(q);
            const assessments = querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));

            assessments.sort((a, b) => {
                const tA = new Date(a.timestamp || 0).getTime();
                const tB = new Date(b.timestamp || 0).getTime();
                return tB - tA;
            });

            return assessments;
        } catch (error) {
            console.error("Error getting assessments:", error);
            throw error;
        }
    },

    // --- CREATIVE WORKS ---

    getCreativeWorksByClient: async (clientId) => {
        try {
            const q = query(
                collection(db, COLLECTIONS.CREATIVE_WORKS),
                where("clientId", "==", clientId)
            );

            const querySnapshot = await getDocs(q);
            const works = querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));

            works.sort((a, b) => {
                const tA = a.timestamp || "";
                const tB = b.timestamp || "";
                return tB.localeCompare(tA);
            });

            return works;
        } catch (error) {
            console.error("Error getting creative works:", error);
            throw error;
        }
    },

    saveCreativeWork: async (workData) => {
        try {
            const docRef = doc(collection(db, COLLECTIONS.CREATIVE_WORKS));

            let ts = Timestamp.now();
            if (workData.timestamp) {
                ts = typeof workData.timestamp === 'string' ?
                    Timestamp.fromDate(new Date(workData.timestamp)) :
                    workData.timestamp;
            }

            const newWork = {
                ...workData,
                id: docRef.id,
                timestamp: ts
            };
            await setDoc(docRef, newWork);
            return FirebaseService.normalizeData({ id: docRef.id, data: () => newWork });
        } catch (error) {
            console.error("Error saving creative work:", error);
            throw error;
        }
    },

    // --- AT LAB EXPERIMENTS ---

    getAtLabSessions: async (clientId) => {
        try {
            const q = query(
                collection(db, COLLECTIONS.AT_LAB),
                where("clientId", "==", clientId)
            );
            const querySnapshot = await getDocs(q);
            const sessions = querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));

            sessions.sort((a, b) => {
                const tA = new Date(a.timestamp || 0).getTime();
                const tB = new Date(b.timestamp || 0).getTime();
                return tB - tA;
            });
            return sessions;
        } catch (error) {
            console.error("Error getting AT lab sessions:", error);
            throw error;
        }
    },

    saveAtLabSession: async (clientId, sessionData) => {
        try {
            const docRef = doc(collection(db, COLLECTIONS.AT_LAB));
            const newSession = {
                ...sessionData,
                clientId: clientId,
                id: docRef.id,
                timestamp: Timestamp.now()
            };
            await setDoc(docRef, newSession);
            return FirebaseService.normalizeData({ id: docRef.id, data: () => newSession });
        } catch (error) {
            console.error("Error saving AT lab session:", error);
            throw error;
        }
    },

    // --- PUBLIC INSIGHTS (Coda senza login) ---

    savePublicInsight: async (insightData) => {
        try {
            const docRef = doc(collection(db, COLLECTIONS.PUBLIC_INSIGHTS));
            const newInsight = {
                ...insightData,
                id: docRef.id,
                timestamp: Timestamp.now()
            };
            await setDoc(docRef, newInsight);
            return FirebaseService.normalizeData({ id: docRef.id, data: () => newInsight });
        } catch (error) {
            console.error("Error saving public insight:", error);
            throw error;
        }
    },

    getPublicInsightsForCounselor: async (counselorId) => {
        try {
            const q = query(
                collection(db, COLLECTIONS.PUBLIC_INSIGHTS),
                where("counselorId", "==", counselorId)
            );
            const querySnapshot = await getDocs(q);
            const insights = querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));
            
            insights.sort((a, b) => {
                const tA = new Date(a.timestamp || 0).getTime();
                const tB = new Date(b.timestamp || 0).getTime();
                return tB - tA;
            });
            
            return insights;
        } catch (error) {
            console.error("Error getting public insights:", error);
            throw error;
        }
    },

    deletePublicInsight: async (insightId) => {
        try {
            await deleteDoc(doc(db, COLLECTIONS.PUBLIC_INSIGHTS, insightId));
            return true;
        } catch (error) {
            console.error("Error deleting public insight:", error);
            throw error;
        }
    },

    // --- GROUP COUNSELING REPORTS ---

    saveGroupReport: async (reportData) => {
        try {
            const newDocRef = doc(collection(db, COLLECTIONS.GROUP_REPORTS));
            const currentUser = auth.currentUser;
            const dataToSave = {
                ...reportData,
                id: newDocRef.id,
                counselorId: reportData.counselorId || currentUser?.uid || '',
                counselorEmail: reportData.counselorEmail || currentUser?.email || '',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            await setDoc(newDocRef, dataToSave);
            return FirebaseService.normalizeData({
                id: newDocRef.id,
                data: () => ({ ...dataToSave, createdAt: new Date().toISOString() })
            });
        } catch (error) {
            console.error("Error saving group report:", error);
            throw error;
        }
    },

    updateGroupReport: async (reportId, reportData) => {
        try {
            const reportRef = doc(db, COLLECTIONS.GROUP_REPORTS, reportId);
            const updatePayload = {
                ...reportData,
                updatedAt: serverTimestamp()
            };
            await updateDoc(reportRef, updatePayload);
            return { id: reportId, ...reportData };
        } catch (error) {
            console.error("Error updating group report:", error);
            throw error;
        }
    },

    deleteGroupReport: async (reportId) => {
        try {
            await deleteDoc(doc(db, COLLECTIONS.GROUP_REPORTS, reportId));
            return true;
        } catch (error) {
            console.error("Error deleting group report:", error);
            throw error;
        }
    },

    getAllGroupReports: async (counselorId = null) => {
        try {
            const currentUser = auth.currentUser;
            let q;
            const isSuperAdmin = currentUser?.email && ['nuvola@awarenesstool.it', 'marilisa.delve@gmail.com'].includes(currentUser.email.toLowerCase());

            if (isSuperAdmin && (!counselorId || counselorId === 'all')) {
                q = query(collection(db, COLLECTIONS.GROUP_REPORTS));
            } else {
                const targetUid = counselorId || currentUser?.uid;
                if (targetUid) {
                    q = query(collection(db, COLLECTIONS.GROUP_REPORTS), where("counselorId", "==", targetUid));
                } else {
                    q = query(collection(db, COLLECTIONS.GROUP_REPORTS));
                }
            }

            const snapshot = await getDocs(q);
            const reports = snapshot.docs.map(d => FirebaseService.normalizeData(d));

            // Sort newest first
            reports.sort((a, b) => {
                const dateA = new Date(a.date || a.createdAt || 0).getTime();
                const dateB = new Date(b.date || b.createdAt || 0).getTime();
                return dateB - dateA;
            });

            return reports;
        } catch (error) {
            console.error("Error getting group reports:", error);
            throw error;
        }
    },

    // --- ALL ASSESSMENTS (Analytics / People Care) ---
    getAllAssessments: async () => {
        try {
            const querySnapshot = await getDocs(collection(db, COLLECTIONS.ASSESSMENTS));
            return querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));
        } catch (error) {
            console.error("Error getting all assessments:", error);
            return [];
        }
    },

    // --- ORGANIZATIONS (Enti & Aziende) ---
    getAllOrganizations: async () => {
        try {
            const querySnapshot = await getDocs(collection(db, COLLECTIONS.ORGANIZATIONS));
            const orgs = querySnapshot.docs.map(doc => FirebaseService.normalizeData(doc));
            if (orgs.length === 0) {
                return [DEFAULT_BUENAVISTA_ORG];
            }
            // Ensure default BuenaVista is always available if not present
            if (!orgs.some(o => o.id === 'buenavista')) {
                orgs.unshift(DEFAULT_BUENAVISTA_ORG);
            }
            return orgs;
        } catch (error) {
            console.warn("Using default organizations fallback:", error);
            return [DEFAULT_BUENAVISTA_ORG];
        }
    },

    saveOrganization: async (orgData) => {
        try {
            const id = orgData.id || `org_${Date.now()}`;
            const docRef = doc(db, COLLECTIONS.ORGANIZATIONS, id);
            const payload = {
                ...orgData,
                id,
                updatedAt: Timestamp.now()
            };
            await setDoc(docRef, payload, { merge: true });
            return payload;
        } catch (error) {
            console.error("Error saving organization:", error);
            throw error;
        }
    },

    deleteOrganization: async (orgId) => {
        try {
            await deleteDoc(doc(db, COLLECTIONS.ORGANIZATIONS, orgId));
            return true;
        } catch (error) {
            console.error("Error deleting organization:", error);
            throw error;
        }
    }
};